// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {RefObject, useContext, useState} from 'react';
import * as React from 'react';

import {
  useFetchArtifactCollectionTriggersQuery,
  useFetchProjectTriggersQuery,
} from '../../../../generated/graphql';
import {
  PremiumFeatureTooltip,
  PremiumFeatureToolTipText,
  useIsAutomationEnabled,
} from '../../../../util/accounts/pricing';
import {propagateErrorsContext} from '../../../../util/errors';
import {trackCreateTriggerClicked} from '../../../../util/navigation';
import {CreateTriggerDrawerForCollection} from '../../../ArtifactCollectionSettings/CreateTriggerModal';
import {getTriggerDetails} from '../../../ArtifactCollectionSettings/utils';
import {getTriggerDrawerInitForCollection} from '../../../Launch/Triggers/CreateTriggerDrawer/EventStore';
import {TriggersTable} from '../../../Launch/Triggers/TriggersTable/TriggersTable';
import {useIsProjectReadOnly} from '../../../Registries/common/hooks';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import * as S from './style';

type TriggersOverviewTableProps = {
  entityName: string;
  projectName: string;
  projectID: string;
  collectionID: string;
  collectionName: string;
  inputRef: RefObject<HTMLDivElement> | null;
};

export const TriggersOverviewTable = ({
  projectName,
  entityName,
  projectID,
  collectionID,
  collectionName,
  inputRef,
}: TriggersOverviewTableProps) => {
  const [addTriggerModalOpen, setAddTriggerModalOpen] = useState(false);
  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName,
      entityName,
    },
    context: propagateErrorsContext(),
  });
  const collectionTriggersQuery = useFetchArtifactCollectionTriggersQuery({
    variables: {
      artifactCollectionID: collectionID,
    },
    context: propagateErrorsContext(),
  });
  const projectScopedTriggers =
    projectTriggersQuery?.data?.project?.triggers.filter(
      t => t.scope.__typename === 'Project'
    ) ?? [];
  const acScopedTriggers =
    collectionTriggersQuery?.data?.artifactCollection?.triggers.edges.map(
      t => t?.node
    ) ?? [];
  const triggers = [...acScopedTriggers, ...projectScopedTriggers];
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(entityName, projectName);
  const {selectedAccount} = useContext(AccountSelectorContext);
  const hasAutomationAccess = useIsAutomationEnabled(selectedAccount);

  if (
    projectTriggersQuery.loading ||
    !projectTriggersQuery.data ||
    collectionTriggersQuery.loading ||
    !collectionTriggersQuery.data ||
    isProjectReadOnlyLoading
  ) {
    return null;
  }

  const existingAutomations = getTriggerDetails(
    projectTriggersQuery,
    collectionTriggersQuery
  );

  return (
    <>
      {triggers!.length > 0 ? (
        <div ref={inputRef}>
          <S.OverviewPanelSectionHeader>
            <S.OverviewPanelSectionHeaderTitle>
              Automations
            </S.OverviewPanelSectionHeaderTitle>
            {!readOnly && (
              <S.TriggerContainer>
                <Button
                  disabled={!hasAutomationAccess}
                  tooltip={
                    !hasAutomationAccess ? (
                      <PremiumFeatureToolTipText
                        isPremiumFeatureDisabled={true}
                      />
                    ) : undefined
                  }
                  icon="add-new"
                  size="medium"
                  variant="primary"
                  onClick={() => {
                    setAddTriggerModalOpen(true);
                    trackCreateTriggerClicked(
                      'Model registry trigger table header'
                    );
                  }}>
                  <>
                    Create automation
                    <PremiumFeatureTooltip
                      isPremiumFeatureDisabled={!hasAutomationAccess}
                    />
                  </>
                </Button>
              </S.TriggerContainer>
            )}
          </S.OverviewPanelSectionHeader>
          <TriggersTable
            projectTriggersQuery={projectTriggersQuery}
            artifactCollectionTriggersQuery={collectionTriggersQuery}
            baseTriggerDrawerInit={getTriggerDrawerInitForCollection(
              projectID,
              projectName,
              entityName,
              collectionID,
              collectionName,
              existingAutomations
            )}
            readOnly={readOnly}
          />
        </div>
      ) : (
        <>
          <S.OverviewPanelSectionHeader>
            <S.OverviewPanelSectionHeaderTitle>
              Automations
            </S.OverviewPanelSectionHeaderTitle>
          </S.OverviewPanelSectionHeader>
          <S.EmptyTriggersTable>
            <S.SettingsOverviewText mainText={true} size="medium">
              No automations yet
            </S.SettingsOverviewText>
            <S.SettingsOverviewText>
              Automate actions based on changes in the collection.
            </S.SettingsOverviewText>
            {!readOnly && (
              <Button
                disabled={!hasAutomationAccess}
                tooltip={
                  !hasAutomationAccess ? (
                    <PremiumFeatureToolTipText
                      isPremiumFeatureDisabled={true}
                    />
                  ) : undefined
                }
                icon="add-new"
                size="medium"
                variant="primary"
                onClick={() => {
                  setAddTriggerModalOpen(true);
                  trackCreateTriggerClicked(
                    'Model registry empty trigger table section'
                  );
                }}
                style={{marginTop: '8px'}}>
                <>
                  Create automation
                  <PremiumFeatureTooltip
                    isPremiumFeatureDisabled={!hasAutomationAccess}
                  />
                </>
              </Button>
            )}
          </S.EmptyTriggersTable>
        </>
      )}
      {addTriggerModalOpen && (
        <CreateTriggerDrawerForCollection
          open={addTriggerModalOpen}
          onClose={() => setAddTriggerModalOpen(false)}
          projectName={projectName}
          entityName={entityName}
          projectID={projectID}
          artifactCollectionID={collectionID}
          artifactCollectionName={collectionName}
        />
      )}
    </>
  );
};
